import styled from "styled-components";
import React, { PropsWithChildren, useContext } from "react";
import { GetStaticProps } from "next";
import { ParsedUrlQuery } from "querystring";
import axios from "axios";
import DOMPurify from "isomorphic-dompurify";
import { sizePx } from "../utils/devices";
import logger from "../services/logger";
import { Page } from "../types/page";
import Menu from "../components/Global/Menu/Menu";
import { Data, MenuModel, SeoModel } from "../types/models";
import { WYSIWYG } from "../globalStyles";
import Meta from "../components/Global/Meta";
import { fullUrl } from "../utils/urlHelper";
import { useRouter } from "next/router";
import { GlobalContext } from "../helpers/context";

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.light};
  @media (max-width: ${sizePx.tablet}) {
    flex-direction: column;
    padding-bottom: 100px;
  }
`;

const Main = styled.div`
  position: relative;
  background: white;
  box-shadow: 0px 5px 9px 3px #e7e7e7;
  border-radius: 1px;
  padding: 30px;
  flex-basis: 50%;
  z-index: 30;
  min-height: 250px;
  max-width: 1200px;
  @media (max-width: ${sizePx.tablet}) {
    padding: 30px;
  }
  @media (max-width: ${sizePx.laptop}) {
    flex-basis: 80%;
  }
`;

const Column = styled.div`
  position: relative;
  padding: 30px;
  flex-basis: 25%;
  max-width: 400px;
  @media (max-width: ${sizePx.tablet}) {
    max-width: 100%;
    z-index: 40;
    width: 100%;
    padding: 0;
  }
`;

const LeftColumn = styled(Column)<{ isHeaderNavOpen: boolean }>`
  @media (max-width: ${sizePx.laptop}) {
    ${({ isHeaderNavOpen }) => isHeaderNavOpen && `z-index:0;`}
    flex-basis: 20%;
  }
`;
const RightColumn = styled(Column)`
  @media (max-width: ${sizePx.laptop}) {
    flex-basis: 0;
    padding: 0;
  }
`;

const PageTitle = styled.h1`
  margin-top: 0;
  text-align: left;
`;

interface Props {
  data?: {
    title: string;
    slug: string;
    content: string;
    menu?: Data<MenuModel>;
    seo: SeoModel | null;
  };
}

const Page: Page = ({ data }: PropsWithChildren<Props>) => {
  const router = useRouter();
  const { isHeaderNavOpen } = useContext(GlobalContext);

  return (
    <>
      <Meta
        title={data?.title}
        description={data?.seo?.metaDescription}
        url={fullUrl(router.asPath)}
        imgUrl={fullUrl(
          data?.seo?.metaImage?.data?.attributes?.formats?.medium?.url,
          true
        )}
      />
      <PageContainer>
        <LeftColumn isHeaderNavOpen={isHeaderNavOpen}>
          {data?.menu?.data && <Menu menu={data?.menu} />}
        </LeftColumn>
        <Main>
          <PageTitle>{data?.title}</PageTitle>
          <WYSIWYG
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(data?.content || ""),
            }}
          />
        </Main>
        <RightColumn></RightColumn>
      </PageContainer>
    </>
  );
};

interface Params extends ParsedUrlQuery {
  url: string[];
}

export const getServerSideProps: GetStaticProps = async ({ params }) => {
  try {
    const { url } = params as Params;

    const res = await axios.get(
      `${
        process.env.BACKEND_API_URL
      }/pages?populate=seo.metaImage.media&populate=menu.links.page&filters[url]=/${url.join(
        "/"
      )}`
    );

    const data = await res.data?.data;
    if (data?.length < 1) {
      return {
        notFound: true,
      };
    }

    return {
      props: { data: data[0].attributes },
    };
  } catch (e) {
    logger.error(e);
    throw e;
  }
};

export default Page;
